import { ButtonBase, Typography, useTheme } from "@mui/material";
import { CornerUpRight } from "lucide-react";
import type { FC } from "react";
import { BalanceLeadSentNote } from "web_myplann/Finances/Components/BalanceLeadSentNote";
import { useBalanceContactLead } from "web_myplann/Finances/Hooks/useBalanceContactLead";
import { useOnlineStatus } from "web_myplann/Infrastructure/Hooks/useOnlineStatus";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { ContactAgentDialog } from "web_myplann/Shared/Components/ContactAgentDialog";

const R = Resources.Finances;

type Props = {
	positive: boolean;
	income: number;
	expense: number;
	size: "sm" | "md";
};

export const BalanceLeadAction: FC<Props> = ({
	positive,
	income,
	expense,
	size,
}) => {
	const theme = useTheme();
	const { t } = useResource();
	const isOnline = useOnlineStatus();
	const {
		isDialogOpen,
		isPending,
		sentAt,
		onOpenDialog,
		onCloseDialog,
		onContactAgent,
	} = useBalanceContactLead({ income, expense });

	const isSmall = size === "sm";
	const color = isOnline ? theme.palette.blue[600] : theme.palette.grey[400];

	return (
		<>
			<ButtonBase
				disableRipple
				disabled={!isOnline}
				onClick={onOpenDialog}
				sx={{
					alignSelf: "flex-start",
					gap: theme.spacingTokens.xs,
					mt: theme.spacingTokens.sm,
					py: theme.spacingTokens.xs,
					"&:active": { opacity: theme.pressedOpacity },
				}}
			>
				<Typography
					variant={isSmall ? "captionStrong" : "bodyStrong"}
					color={color}
				>
					{positive
						? t(R.Recommendation.surplusAction)
						: t(R.Recommendation.deficitAction)}
				</Typography>
				<CornerUpRight
					size={isSmall ? theme.iconSize.xxs : theme.iconSize.xs}
					color={color}
					aria-hidden
				/>
			</ButtonBase>
			{sentAt && <BalanceLeadSentNote sentAt={sentAt} />}
			<ContactAgentDialog
				open={isDialogOpen}
				isPending={isPending}
				onClose={onCloseDialog}
				onConfirm={onContactAgent}
				title={t(R.ContactDialog.title)}
				description={t(R.ContactDialog.description)}
				buttonLabel={t(R.ContactDialog.button)}
				successTitle={t(R.ContactDialog.successTitle)}
				successDescription={t(R.ContactDialog.successDescription)}
			/>
		</>
	);
};
